import React, { useState, useCallback } from 'react';
import { View, Image, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import Text from '../../v2BaseComponents/Text';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import * as Card from '../../v2BaseComponents/Card';
import SearchInput from '../../v2BaseComponents/SearchInput';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import HeaderContainer from '../../v2BaseComponents/HeaderContainer';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import { StandardPrompt, PromptActions } from '../../v2BaseComponents/Prompt';
import { ContentStack } from '../../v2BaseComponents/Stack';
import Icon from '../../v2BaseComponents/Icon';
import { PRFlatList, PRScrollView } from '../Scroll';
import useFastenCatalogSearch from '../../../hooks/api/useFastenCatalogSearch';
import { APITypes } from '../../../primary-record-types';
import Link from '../../v2BaseComponents/Link';
import { getIntl } from '../../../utils/intl';
import { STATE_OPTIONS } from './stateOptions';
import { getFastenLogoUrl } from '../../../utils/fastenUtils';
import useRecordEvent from '../../../hooks/useRecordEvent';

type CatalogEntry = APITypes.ehrIntegrationsService.FastenCatalogSearchEntry;
type CatalogPortal = CatalogEntry['portals'][number];

const intl = getIntl('fasten-search', {
    searchPlaceholder: 'Search for your health system...',
    noResultsTitle: 'No results found.',
    noResultsMessage:
        "Try adjusting your search by using your healthcare system or clinic name, including the state, or searching for your provider's name. You may also want to check for combined words or alternate spellings.",
    selectPortal: 'Select your patient portal',
    connectToPortal: 'Connect to Portal?',
    connectConfirmation: 'You will be redirected to sign in with {portalName} to securely connect your records.',
    connect: 'Connect',
    connecting: 'Connecting...',
    connectingMessage: 'Opening a secure connection window...',
    cancel: 'Cancel',
    back: 'Back to search',
    instructionsHeader: 'Search for Your Health System',
    instructionsTip1Pretext: 'Good Place to Start: ',
    instructionsTip1: 'Type in the health system or hospital name you know.',
    instructionsTip2Pretext: 'Broaden Your Search: ',
    instructionsTip2: 'Unsure of the exact name? Try entering the full name of your state for a comprehensive list.',
    instructionsTip3Pretext: 'Think Big: ',
    instructionsTip3:
        "Many hospitals and doctor's offices belong to larger health networks. Try searching for the bigger health system.",
    facilitatedBy: 'Connection facilitated by Fasten Health.',
});

export type FastenSearchSelection = {
    brandId: string;
    portalId: string;
    endpointId: string;
    brandName: string;
    portalName: string;
    platformType: string;
    /** The Fasten mode the catalog entry came from; the connect must use the same mode. */
    apiMode: APITypes.ehrIntegrationsService.FastenApiMode;
};

export type FastenSearchProps = {
    onSelect: (selection: FastenSearchSelection) => void;
    isConnecting?: boolean;
    onSearchActiveChange?: (isSearchActive: boolean) => void;
};

type ViewState = { step: 'search' } | { step: 'portal-picker'; brand: CatalogEntry };

const STATE_ABBREV_TO_NAME: Record<string, string> = Object.fromEntries(
    STATE_OPTIONS.filter((opt) => opt.value && opt.value !== '__separator__').map((opt) => [opt.value, opt.label]),
);

/** Format locations array into a readable string (e.g. "Indiana" or "Arizona, Florida, Minnesota") */
function formatLocations(locations: string[]): string {
    if (!locations.length) return 'Nationwide';
    if (locations.length === 1) {
        return locations[0] === 'ALL' ? 'Nationwide' : STATE_ABBREV_TO_NAME[locations[0]] || locations[0];
    }
    if (locations.includes('ALL')) return 'Nationwide';
    const names = locations.map((loc) => STATE_ABBREV_TO_NAME[loc] || loc);
    if (names.length <= 3) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
}

const BrandLogo: React.FC<{ brandId: string; brandName: string; size?: 'small' | 'large' }> = ({
    brandId,
    brandName,
    size = 'small',
}) => {
    const [hasError, setHasError] = useState(false);
    const containerStyle = size === 'large' ? styles.logoContainerLarge : styles.logoContainer;
    const imageStyle = size === 'large' ? styles.logoLarge : styles.logo;

    if (hasError) {
        return (
            <View style={[containerStyle, styles.logoPlaceholderBg]}>
                <Text variant={size === 'large' ? 'h3' : 'h4'} color="Gray-Text">
                    {brandName.charAt(0)}
                </Text>
            </View>
        );
    }

    return (
        <View style={containerStyle}>
            <Image
                source={{ uri: getFastenLogoUrl(brandId) }}
                style={imageStyle}
                resizeMode="contain"
                onError={() => setHasError(true)}
            />
        </View>
    );
};

const FastenSearch: React.FC<FastenSearchProps> = ({ onSelect, isConnecting, onSearchActiveChange }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const recordEvent = useRecordEvent();
    const {
        entries: searchResults,
        isLoading,
        isFetching,
        entriesApiMode,
    } = useFastenCatalogSearch({ search: searchQuery });

    // Notify parent when search becomes active/inactive
    const prevActive = React.useRef(false);
    React.useEffect(() => {
        const isActive = !!searchQuery && !isLoading;
        if (isActive !== prevActive.current) {
            prevActive.current = isActive;
            onSearchActiveChange?.(isActive);
        }
    }, [searchQuery, isLoading, onSearchActiveChange]);
    const isBackgroundFetching = isFetching && !isLoading && searchResults.length > 0;
    const [viewState, setViewState] = useState<ViewState>({ step: 'search' });
    const [pendingSelection, setPendingSelection] = useState<FastenSearchSelection | null>(null);
    const [isConfirmPromptOpen, setIsConfirmPromptOpen] = useState(false);
    const connectClickedRef = React.useRef(false);

    // A brand or portal picked from one mode's catalog is meaningless in the other, so drop any in-progress
    // selection when the sandbox toggle changes the results underneath it.
    React.useEffect(() => {
        setViewState({ step: 'search' });
        setPendingSelection(null);
        setIsConfirmPromptOpen(false);
    }, [entriesApiMode]);

    const confirmAndSelect = useCallback((selection: FastenSearchSelection) => {
        connectClickedRef.current = false;
        setPendingSelection(selection);
        setIsConfirmPromptOpen(true);
    }, []);

    const handleBrandPress = useCallback(
        (brand: CatalogEntry) => {
            recordEvent('portalSearch', {
                portalSearchTerm: searchQuery,
            });

            if (brand.portals.length === 1) {
                const portal = brand.portals[0];
                const endpoint = portal.endpoints[0];
                if (endpoint) {
                    confirmAndSelect({
                        brandId: brand.brand_id,
                        portalId: portal.id,
                        endpointId: endpoint.id,
                        brandName: brand.name,
                        portalName: portal.name,
                        platformType: endpoint.platform_type,
                        apiMode: entriesApiMode,
                    });
                }
            } else {
                setViewState({ step: 'portal-picker', brand });
            }
        },
        [confirmAndSelect, recordEvent, searchQuery, entriesApiMode],
    );

    const handlePortalPress = useCallback(
        (brand: CatalogEntry, portal: CatalogPortal) => {
            const endpoint = portal.endpoints[0];
            if (endpoint) {
                confirmAndSelect({
                    brandId: brand.brand_id,
                    portalId: portal.id,
                    endpointId: endpoint.id,
                    brandName: brand.name,
                    portalName: portal.name,
                    platformType: endpoint.platform_type,
                    apiMode: entriesApiMode,
                });
            }
        },
        [confirmAndSelect, entriesApiMode],
    );

    const handleBack = useCallback(() => {
        setViewState({ step: 'search' });
    }, []);

    // Portal picker view — matches Fasten widget screenshot 2 & 4
    if (viewState.step === 'portal-picker') {
        const { brand } = viewState;
        const locationText = formatLocations(brand.locations);

        return (
            <View style={styles.container}>
                <PRFlatList
                    data={brand.portals.filter((p) => p.endpoints.length > 0)}
                    keyExtractor={(item) => item.id}
                    style={{ flex: 1, width: '100%' }}
                    contentContainerStyle={{ padding: mainContentPadding }}
                    ItemSeparatorComponent={() => <View style={{ height: contentStackSpacing }} />}
                    renderItem={({ item: portal }) => (
                        <Card.Card
                            onPress={() => handlePortalPress(brand, portal)}
                            testID="fasten-portal-option"
                            style={styles.portalCard}
                        >
                            <Card.CardContent>
                                {portal.endpoints.length > 0 && (
                                    <Card.CardChip title={portal.endpoints[0].platform_type} isSecondaryChip />
                                )}
                                <Card.CardHeader>{portal.name}</Card.CardHeader>
                            </Card.CardContent>
                        </Card.Card>
                    )}
                    ListHeaderComponent={
                        <View style={{ marginBottom: contentStackSpacing }}>
                            <Card.Card style={styles.brandDetailCard}>
                                <Stack horizontal align="center" space={3}>
                                    <BrandLogo brandId={brand.brand_id} brandName={brand.name} size="large" />
                                    <Text variant="h4">{brand.name}</Text>
                                </Stack>
                                {brand.brand_website ? (
                                    <View style={styles.detailRow}>
                                        <Icon name="Globe" size="button-icon" color="Gray-Text" />
                                        <Link
                                            onPress={() => {
                                                if (Platform.OS === 'web') {
                                                    window.open(brand.brand_website, '_blank', 'noopener');
                                                } else {
                                                    import('expo-linking').then((Linking) =>
                                                        Linking.openURL(brand.brand_website!),
                                                    );
                                                }
                                            }}
                                            variant="body-2"
                                            linkColor="blue"
                                            style={{ marginLeft: 6 }}
                                        >
                                            {brand.brand_website}
                                        </Link>
                                    </View>
                                ) : null}
                                {locationText ? (
                                    <View style={styles.detailRow}>
                                        <Icon name="MapPin" size="button-icon" color="Gray-Text" />
                                        <Text variant="body-2" color="Gray-Text" style={{ marginLeft: 6 }}>
                                            {locationText}
                                        </Text>
                                    </View>
                                ) : null}
                            </Card.Card>
                        </View>
                    }
                    ListFooterComponent={
                        <View style={{ marginTop: contentStackSpacing }}>
                            <PromptActions
                                actions={[
                                    {
                                        title: intl('back'),
                                        onPress: handleBack,
                                        variant: 'secondary',
                                        icon: 'ArrowLeft',
                                    },
                                ]}
                            />
                        </View>
                    }
                />

                <StandardPrompt
                    isOpen={isConfirmPromptOpen}
                    setIsOpen={(open) => {
                        if (!open && (isConnecting || connectClickedRef.current)) return;
                        setIsConfirmPromptOpen(open);
                    }}
                    title={intl('connectToPortal')}
                >
                    {pendingSelection && (
                        <ConfirmationContent
                            selection={pendingSelection}
                            onConnect={() => {
                                connectClickedRef.current = true;
                                onSelect(pendingSelection);
                            }}
                            onCancel={() => setIsConfirmPromptOpen(false)}
                            isConnecting={isConnecting}
                        />
                    )}
                </StandardPrompt>
            </View>
        );
    }

    // Search view
    const renderSearchContent = () => {
        // Empty state — show instructions when no search query
        if (!searchQuery) {
            return (
                <PRScrollView
                    style={{ flex: 1, width: '100%' }}
                    contentContainerStyle={{ padding: mainContentPadding }}
                >
                    <ContentStack>
                        <Text variant="h4">{intl('instructionsHeader')}</Text>
                        {[
                            { pretext: intl('instructionsTip1Pretext'), text: intl('instructionsTip1') },
                            { pretext: intl('instructionsTip2Pretext'), text: intl('instructionsTip2') },
                            { pretext: intl('instructionsTip3Pretext'), text: intl('instructionsTip3') },
                        ].map(({ pretext, text }) => (
                            <Text key={text} variant="body-1">
                                <Text variant="bold-body-1">{pretext}</Text>
                                <Text variant="body-1">{text}</Text>
                            </Text>
                        ))}
                        <Text variant="body-2" color="Gray-Text">
                            {intl('facilitatedBy')}
                        </Text>
                    </ContentStack>
                </PRScrollView>
            );
        }

        // No results
        if (searchResults.length === 0 && !isLoading) {
            return (
                <PRScrollView
                    style={{ flex: 1, width: '100%' }}
                    contentContainerStyle={{ padding: mainContentPadding }}
                >
                    <ContentStack>
                        <Text variant="h4">{intl('noResultsTitle')}</Text>
                        <Text variant="body-1" color="Gray-Text">
                            {intl('noResultsMessage')}
                        </Text>
                    </ContentStack>
                </PRScrollView>
            );
        }

        // Results list
        return (
            <PRFlatList
                keyExtractor={(item) => item.brand_id}
                data={searchResults.filter((b) => b.portals.some((p) => p.endpoints.length > 0))}
                style={{ flex: 1, width: '100%', opacity: isBackgroundFetching ? 0.5 : 1 }}
                contentContainerStyle={{ padding: mainContentPadding }}
                ItemSeparatorComponent={() => <View style={{ height: contentStackSpacing }} />}
                renderItem={({ item: brand }) => {
                    const locationText = formatLocations(brand.locations);
                    return (
                        <Card.Card
                            onPress={() => handleBrandPress(brand)}
                            testID="fasten-brand-result"
                            style={styles.card}
                        >
                            <Stack horizontal align="center" space={3} style={{ flex: 1 }}>
                                <BrandLogo brandId={brand.brand_id} brandName={brand.name} />
                                <View style={{ flex: 1 }}>
                                    <Text variant="h5">{brand.name}</Text>
                                    {locationText ? (
                                        <Text variant="body-2" color="Gray-Text">
                                            {locationText}
                                        </Text>
                                    ) : null}
                                </View>
                                <Icon name="ChevronRight" size="button-icon" color="Gray-Text" />
                            </Stack>
                        </Card.Card>
                    );
                }}
            />
        );
    };

    return (
        <View style={styles.container}>
            <HeaderContainer>
                <SearchInput
                    testID="fasten-search-input"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    debounceInterval={400}
                    isDisabled={isConnecting}
                    placeholder={intl('searchPlaceholder')}
                    keepPlaceholderOnFocus
                    backgroundColorOverride="White"
                />
            </HeaderContainer>
            <AnimateLoadedContent isLoading={isLoading} style={{ flex: 1, width: '100%' }}>
                {renderSearchContent}
            </AnimateLoadedContent>

            <StandardPrompt
                isOpen={isConfirmPromptOpen}
                setIsOpen={(open) => {
                    // Don't allow closing while connect is in progress
                    if (!open && (isConnecting || connectClickedRef.current)) return;
                    setIsConfirmPromptOpen(open);
                }}
                title={intl('connectToPortal')}
            >
                {pendingSelection && (
                    <ConfirmationContent
                        selection={pendingSelection}
                        onConnect={() => {
                            connectClickedRef.current = true;
                            onSelect(pendingSelection);
                        }}
                        onCancel={() => setIsConfirmPromptOpen(false)}
                        isConnecting={isConnecting}
                    />
                )}
            </StandardPrompt>
        </View>
    );
};

const ConfirmationContent: React.FC<{
    selection: FastenSearchSelection;
    onConnect: () => void;
    onCancel: () => void;
    isConnecting?: boolean;
}> = ({ selection, onConnect, onCancel, isConnecting }) => (
    <ContentStack>
        <Stack horizontal align="center" space={3}>
            <BrandLogo brandId={selection.brandId} brandName={selection.brandName} />
            <View style={{ flex: 1 }}>
                <Text variant="h5">{selection.brandName}</Text>
                <Text variant="body-2" color="Gray-Text">
                    {selection.portalName}
                </Text>
            </View>
        </Stack>
        <Text variant="body-1">{intl('connectConfirmation', { portalName: selection.portalName })}</Text>
        {isConnecting && (
            <Stack horizontal align="center" justify="center" space={2}>
                <ActivityIndicator size="small" color="#4F46E5" />
                <Text variant="body-2" color="Gray-Text">
                    {intl('connectingMessage')}
                </Text>
            </Stack>
        )}
        <PromptActions
            actions={[
                {
                    title: isConnecting ? intl('connecting') : intl('connect'),
                    onPress: onConnect,
                    icon: isConnecting ? undefined : 'ExternalLink',
                    isDisabled: isConnecting,
                },
                {
                    title: intl('cancel'),
                    onPress: onCancel,
                    variant: 'secondary',
                    icon: 'X',
                    isDisabled: isConnecting,
                    show: !isConnecting,
                },
            ]}
        />
    </ContentStack>
);

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
    },
    card: {
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 16,
        paddingRight: 16,
    },
    portalCard: {
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 16,
        paddingRight: 16,
    },
    brandDetailCard: {
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 8,
    },
    logoContainer: {
        width: 48,
        height: 48,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f9f9f9',
        borderRadius: 8,
        overflow: 'hidden',
    },
    logoContainerLarge: {
        width: 64,
        height: 64,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f9f9f9',
        borderRadius: 8,
        overflow: 'hidden',
    },
    logo: {
        width: 40,
        height: 40,
    },
    logoLarge: {
        width: 52,
        height: 52,
    },
    logoPlaceholderBg: {
        backgroundColor: '#E5E7EB',
    },
});

export default React.memo(FastenSearch);
